/**
 * Store-layer types for agent chat state.
 */

import type {MessageRole, ReadonlyJSONValue} from '../providerMessage';

export type RunOutcome = 'finished' | 'errored' | 'cancelled';

export interface AgentChatState {
  threads: Record<string, Thread>;
  activeThreadId: string | null;
}

export interface Thread {
  id: string;
  title: string | null;
  status: 'idle' | 'running' | 'error';
  error: string | null;
  rootTurnId: string | null;
  messageTree: MessageTree;
  currentRunId: string | null;
  currentMessageId: string | null;
  runOutcomes: Record<string, RunOutcome>;
  createdAt: number;
  updatedAt: number;
}

export interface MessageTree {
  /** activeChildId maps parent IDs to their active child ID. */
  activeChildId: Record<string, string>;
  /** children maps parent IDs to their ordered child IDs. */
  children: Record<string, string[]>;
  /** currentNodeId is the latest in the active branch. */
  currentNodeId: string | null;
  /** nodes maps IDs to the message nodes. */
  nodes: Record<string, MessageNode>;
  /** parentId maps node IDs to their parent IDs (null for root-level nodes). */
  parentId: Record<string, string | null>;
  /** rootChildren lists all top-level node IDs. */
  rootChildren: string[];
}

export interface MessageNode {
  id: string;
  role: MessageRole;
  content: ContentPart[];
  createdAt: number;
}

export type ContentPart = TextPart | ThinkingPart | ToolCallPart;

export interface TextPart {
  type: 'text';
  text: string;
  status: 'in-progress' | 'complete';
}

export interface ThinkingPart {
  type: 'thinking';
  text: string;
  status: 'in-progress' | 'complete';
}

export interface ToolCallPart {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  argsText: string;
  args: Record<string, ReadonlyJSONValue> | null;
  status: 'pending' | 'executing' | 'done' | 'error';
  result?: string;
  isError?: boolean;
}

export function createDefaultState(): AgentChatState {
  return {
    threads: {},
    activeThreadId: null,
  };
}

export function createEmptyThread(id: string, now = Date.now()): Thread {
  return {
    id,
    title: null,
    status: 'idle',
    error: null,
    rootTurnId: null,
    messageTree: createEmptyTree(),
    currentRunId: null,
    currentMessageId: null,
    runOutcomes: {},
    createdAt: now,
    updatedAt: now,
  };
}

export function createEmptyTree(): MessageTree {
  return {
    nodes: {},
    parentId: {},
    children: {},
    rootChildren: [],
    currentNodeId: null,
    activeChildId: {},
  };
}
